import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ThemeContext } from "../App";
import "../css/header.scss";
import arIcon from "../data/icons/arabic.png";
import azIcon from "../data/icons/az.png";
import cnIcon from "../data/icons/china.png";
import deIcon from "../data/icons/de.png";
import frIcon from "../data/icons/france.png";
import jpIcon from "../data/icons/jp.png";
import kgIcon from "../data/icons/kg.png";
import koIcon from "../data/icons/ko.png";
import kzIcon from "../data/icons/kz.png";
import mnIcon from "../data/icons/mn.png";
import ruIcon from "../data/icons/russia.png";
import seIcon from "../data/icons/se.png";
import esIcon from "../data/icons/spain.png";
import tjIcon from "../data/icons/tj.png";
import tmIcon from "../data/icons/tm.png";
import trIcon from "../data/icons/turkey.png";
import enIcon from "../data/icons/us.png";
import uzIcon from "../data/icons/uz.png";

const Header = ({ data }) => {
  const { setLanguage, language } = useContext(ThemeContext);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const isSignup = location.pathname === "/signup";
  useEffect(() => {
    const handleScroll = () => {
      if (langMenuOpen) setLangMenuOpen(false);
    };

    const handleClickOutside = (event) => {
      if (langMenuOpen && !event.target.closest(".header__lang")) {
        setLangMenuOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [langMenuOpen]);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    localStorage.setItem("lingo_portal_app_lang", lang);
    setLangMenuOpen(false);
  };

  const getLanguageName = (lang) => {
    if (lang === "en") return "English";
    if (lang === "ko") return "한국어";
    if (lang === "jp") return "日本語";
    if (lang === "de") return "Deutsch";
    if (lang === "se") return "Svenska";
    if (lang === "ru") return "Русский";
    if (lang === "zh") return "中文";
    if (lang === "tr") return "Türkçe";
    if (lang === "ar") return "العربية";
    if (lang === "fr") return "Français";
    if (lang === "es") return "Español";
    if (lang === "kz") return "Қазақша";
    if (lang === "uz") return "Oʻzbekcha";
    if (lang === "az") return "Azərbaycanca";
    if (lang === "tj") return "Тоҷикӣ";
    if (lang === "kg") return "Кыргызча";
    if (lang === "tm") return "Türkmençe";
    return "Монгол";
  };

  const getLanguageIcon = (lang) => {
    if (lang === "en") return enIcon;
    if (lang === "ko") return koIcon;
    if (lang === "jp") return jpIcon;
    if (lang === "de") return deIcon;
    if (lang === "se") return seIcon;
    if (lang === "ru") return ruIcon;
    if (lang === "zh") return cnIcon;
    if (lang === "tr") return trIcon;
    if (lang === "ar") return arIcon;
    if (lang === "fr") return frIcon;
    if (lang === "es") return esIcon;
    if (lang === "kz") return kzIcon;
    if (lang === "uz") return uzIcon;
    if (lang === "az") return azIcon;
    if (lang === "tj") return tjIcon;
    if (lang === "kg") return kgIcon;
    if (lang === "tm") return tmIcon;
    return mnIcon;
  };

  const languages = [
    { code: "en", icon: enIcon, label: "English" },
    { code: "mn", icon: mnIcon, label: "Монгол" },
    { code: "ko", icon: koIcon, label: "한국어" },
    { code: "jp", icon: jpIcon, label: "日本語" },
    { code: "de", icon: deIcon, label: "Deutsch" },
    { code: "se", icon: seIcon, label: "Svenska" },
    { code: "fr", icon: frIcon, label: "Français" },
    { code: "es", icon: esIcon, label: "Español" },
    { code: "ru", icon: ruIcon, label: "Русский" },
    { code: "zh", icon: cnIcon, label: "中文" },
    { code: "tr", icon: trIcon, label: "Türkçe" },
    { code: "kz", icon: kzIcon, label: "Қазақша" },
    { code: "uz", icon: uzIcon, label: "Oʻzbekcha" },
    { code: "az", icon: azIcon, label: "Azərbaycanca" },
    { code: "tj", icon: tjIcon, label: "Тоҷикӣ" },
    { code: "kg", icon: kgIcon, label: "Кыргызча" },
    { code: "tm", icon: tmIcon, label: "Türkmençe" },
    { code: "ar", icon: arIcon, label: "العربية" },
  ];

  const navLinks = [
    { to: "/", label: data.home },
    { to: "/about", label: data.about },
    { to: "/team", label: data.team },
    { to: "/solution", label: data.solution },
    { to: "/tests", label: data.tests },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <>
      <header className="header">
        <Link to="/" className="header__logo-link">
          <img
            src="/images/tomko_logo_color.png"
            alt="Tomko"
            className="header__logo"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="header__nav">
          {navLinks.map(({ to, label }) => (
            <Link
              key={to}
              className={`header__nav-link ${isActive(to) ? "header__nav-link--active" : ""}`}
              to={to}
            >
              {label}
            </Link>
          ))}
        </nav>

        {/* Desktop right section */}
        <div className="header__right">
          <Link to="/signup" className="header__cta">
            {data.read}
          </Link>

          <div
            className={`header__lang ${isSignup ? "header__lang--disabled" : ""}`}
            onClick={() => {
              if (isSignup) return;
              setLangMenuOpen(!langMenuOpen);
            }}
          >
            <div className="header__lang-selector">
              <img
                src={getLanguageIcon(language)}
                className="header__lang-flag"
                alt={getLanguageName(language)}
              />
            </div>

            {langMenuOpen && (
              <div className="header__lang-dropdown">
                {languages.map(({ code, icon, label }) => (
                  <div
                    key={code}
                    className={`header__lang-option ${
                      language === code ? "header__lang-option--active" : ""
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLanguageChange(code);
                    }}
                  >
                    <img src={icon} alt={label} className="header__lang-option-icon" />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile hamburger button */}
        <button
          className="header__hamburger"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </header>

      {/* Overlay */}
      <div
        className={`drawer-overlay ${drawerOpen ? "drawer-overlay--open" : ""}`}
        onClick={() => setDrawerOpen(false)}
      />

      {/* Side Drawer */}
      <div className={`drawer ${drawerOpen ? "drawer--open" : ""}`}>
        <div className="drawer__header">
          <img
            src="/images/tomko_logo_color.png"
            alt="Tomko"
            className="drawer__logo"
          />
          <button
            className="drawer__close-btn"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        <nav className="drawer__nav">
          {navLinks.map(({ to, label }) => (
            <Link
              key={to}
              className={`drawer__nav-link ${isActive(to) ? "drawer__nav-link--active" : ""}`}
              to={to}
              onClick={() => setDrawerOpen(false)}
            >
              {label}
            </Link>
          ))}
          <Link
            to="/school/client"
            className="drawer__cta"
            onClick={() => setDrawerOpen(false)}
          >
            {data.read}
          </Link>
        </nav>

        <div className="drawer__lang-section">
          <p className="drawer__lang-title">Хэл сонгох</p>
          <div className="drawer__lang-list">
            {languages.map(({ code, icon, label }) => (
              <div
                key={code}
                className={`drawer__lang-option ${
                  language === code ? "drawer__lang-option--active" : ""
                }`}
                onClick={() => handleLanguageChange(code)}
              >
                <img src={icon} alt={label} className="drawer__lang-icon" />
                <span>{label}</span>
                {language === code && (
                  <span className="drawer__lang-check">✓</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
